import { AppService } from '../../app.service';
import { UtilityFunctions } from '../../shared/utility-functions';
import { CONSTANT_MESSAGES } from '../../constants/constants-messages';
import { TimeCurrencyService } from './../time-currency.service';
import { EventFacility } from './event-facility.interface';
import { Component } from '@angular/core';

export class EventValidation {
  public validate: any = {};
  public errorMessages: any = {};

  constructor(
    private appService: AppService,
    private utilityFunctions: UtilityFunctions,
    private timeCurrencyService: TimeCurrencyService
  ) {
    this.initializeValidation();
  }

  private initializeValidation(): void {
    this.validate = {
      validEventType: '',
      validName: '',
      validDate: '',
      validIcon: '',
      validAddressLine1: '',
      validPincode: '',
      validCountry: '',
      validOpenTime_hour: '',
      validOpenTime_minute: '',
      validCloseTime_hour: '',
      validCloseTime_minute: '',
      validMaxNumberOfPeople: '',
      validmaxNumberOfPeoplePerSlot: '',
      validWaitlistCount: '',
      GSTValue: '',
      validReminder: '',
      holdBookingDays: '',
      validNotificationAcceptanceHour: '',
      validMemberAmount: '',
      validNonMemberAmount: '',
      validGuestAmount: '',
      validCorpMemberAmount: '',
      validCorpNonMemberAmount: '',
      validMemberChildAmount: '',
      validNonMemberChildAmount: '',
      validGuestChildAmount: '',
      validCorpMemberChildAmount: '',
      validCorpNonMemberChildAmount: ''
    };

    this.errorMessages = {
      eventTypeError: '',
      nameError: '',
      dateError: '',
      iconError: '',
      addressLine1Error: '',
      pincodeError: '',
      countryError: '',
      timingError: '',
      capacityError: '',
      waitlistError: '',
      gstError: '',
      reminderError: '',
      notificationError: '',
      memberAmountError: '',
      nonMemberAmountError: '',
      guestAmountError: '',
      corpMemberAmountError: '',
      corpNonMemberAmountError: '',
      memberChildAmountError: '',
      nonMemberChildAmountError: '',
      guestChildAmountError: '',
      corpMemberChildAmountError: '',
      corpNonMemberChildAmountError: ''
    };
  }

  /**
   * Main validation method for event object
   */
  validateFacilityObj(component: any): boolean {
    let isValid = true;

    if (!this.validateEventBasics(component)) {
      isValid = false;
    }

    if (!this.validateAddressFields(component)) {
      isValid = false;
    }

    if (!this.validateEventTimings(component)) {
      isValid = false;
    }

    if (!this.validateEventCapacity(component)) {
      isValid = false;
    }

    // if (!this.validateMaxBookingPerSlot(component)) {
    //   isValid = false;
    // }

    if (!this.validateAttendeeManagement(component)) {
      isValid = false;
    }

    if (!this.validateWaitlist(component)) {
      isValid = false;
    }

    if (!this.validateGST(component)) {
      isValid = false;
    }

    if (!this.validateNotificationSettings(component)) {
      isValid = false;
    }

    if (!this.validatePricing(component)) {
      isValid = false;
    }

    return isValid;
  }

  /**
   * Validate basic event details
   */
  private validateEventBasics(component: any): boolean {
    let isValid = true;

    if (!component.facility.eventType || component.facility.eventType.trim() === '') {
      this.setValidationRequired('validEventType');
      isValid = false;
    } else {
      this.setValidationSuccess('validEventType');
    }

    if (!component.facility.name || component.facility.name.trim() === '' || component.facility.name.trim().length < 1) {
      this.setValidationRequired('validName');
      isValid = false;
    } else {
      this.setValidationSuccess('validName');
    }

    if (!component.facility.date || component.facility.date === '') {
      this.setValidationRequired('validDate');
      isValid = false;
    } else {
      const selectedDate = new Date(component.facility.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        this.setValidationRequired('validDate');
        isValid = false;
      } else {
        this.setValidationSuccess('validDate');
      }
    }

    if (!component.facility.icon || component.facility.icon === '') {
      this.setValidationRequired('validIcon');
      isValid = false;
    } else {
      this.setValidationSuccess('validIcon');
    }

    return isValid;
  }

  /**
   * Validate address fields with country-specific pincode validation
   */
  private validateAddressFields(component: any): boolean {
    let isValid = true;

    if (!component.facility.addressLine1 || component.facility.addressLine1.trim() === '' || component.facility.addressLine1.trim().length < 1) {
      this.setValidationRequired('validAddressLine1');
      isValid = false;
    } else {
      this.setValidationSuccess('validAddressLine1');
    }

    // Enhanced pincode validation with country-specific rules
    if (!this.validatePincodeWithCountry(component.facility.pincode, component)) {
      isValid = false;
    }

    if (!component.facility.country || component.facility.country.trim() === '') {
      this.setValidationRequired('validCountry');
      isValid = false;
    } else {
      this.setValidationSuccess('validCountry');
    }

    return isValid;
  }

  /**
   * Enhanced pincode validation using TimeCurrencyService with country context
   */
  private validatePincodeWithCountry(pincode: string, component: any): boolean {
    const country = this.getCurrentCountryFromComponent(component);

    if (!this.timeCurrencyService.isPostalCodeRequired(country)) {
      this.setValidationSuccess('validPincode');
      return true;
    }

    if (!pincode || pincode.trim() === '') {
      this.setValidationRequired('validPincode');
      return false;
    }

    if (!this.timeCurrencyService.validatePincodeForCountry(pincode.trim(), country)) {
      this.setValidationRequired('validPincode');
      return false;
    }

    this.setValidationSuccess('validPincode');
    return true;
  }

  /**
   * Get current country from component parameter
   */
  private getCurrentCountryFromComponent(component: any): string {
    if (component &&
      component.facility &&
      component.facility.country &&
      component.facility.country.trim() !== '') {
      return component.facility.country.trim();
    }

    if (component &&
      component.vendor &&
      component.vendor.country &&
      component.vendor.country.trim() !== '') {
      return component.vendor.country.trim();
    }

    return 'Singapore';
  }

  //FOR FUTURE USE - to limit max bookings per slot to event capacity
  // private getEventCapacityFromComponent(component: any): number {
  //   if (component && component.facility && component.facility.eventCapacity) {
  //     return component.facility.eventCapacity;
  //   }
  //   return 0;
  // }

  /**
   * Validate event timings
   */
  private validateEventTimings(component: any): boolean {
    let isValid = true;

    if (!component.openTime_hour || component.openTime_hour === '') {
      this.setValidationRequired('validOpenTime_hour');
      isValid = false;
    } else {
      this.setValidationSuccess('validOpenTime_hour');
    }

    if (!component.openTime_minute || component.openTime_minute === '') {
      this.setValidationRequired('validOpenTime_minute');
      isValid = false;
    } else {
      this.setValidationSuccess('validOpenTime_minute');
    }

    if (!component.closeTime_hour || component.closeTime_hour === '') {
      this.setValidationRequired('validCloseTime_hour');
      isValid = false;
    } else {
      this.setValidationSuccess('validCloseTime_hour');
    }

    if (!component.closeTime_minute || component.closeTime_minute === '') {
      this.setValidationRequired('validCloseTime_minute');
      isValid = false;
    } else {
      this.setValidationSuccess('validCloseTime_minute');
    }

    return isValid;
  }

  /**
   * Validate event capacity
   */
  // private validateMaxBookingPerSlot(component: any): boolean {
  //   let isValid = true;

  //   const maxBookingsPerSlot = component.facility.maxBookingsPerSlot;
  //   const maxBookingsPerSlotStr = maxBookingsPerSlot != null ? String(maxBookingsPerSlot).trim() : '';

  //   if (!maxBookingsPerSlotStr || maxBookingsPerSlotStr === '') {
  //     this.setValidationRequired('validmaxNumberOfPeoplePerSlot');
  //     isValid = false;
  //   } else if (maxBookingsPerSlotStr.toLowerCase() !== 'unlimited') {
  //     const capacity = parseInt(maxBookingsPerSlotStr, 10);
  //     if (isNaN(capacity) || capacity <= 0) {
  //       this.setValidationRequired('validmaxNumberOfPeoplePerSlot');
  //       isValid = false;
  //     } else {
  //       this.setValidationSuccess('validmaxNumberOfPeoplePerSlot');
  //     }
  //   } else {
  //     this.setValidationSuccess('validmaxNumberOfPeoplePerSlot');
  //   }

  //   return isValid;
  // }

  private validateEventCapacity(component: any): boolean {
    let isValid = true;

    const eventCapacity = component.facility.eventCapacity;
    const eventCapacityStr = eventCapacity != null ? String(eventCapacity).trim() : '';

    if (!eventCapacityStr || eventCapacityStr === '') {
      this.setValidationRequired('validEventCapacity');
      isValid = false;
    } else if (eventCapacityStr.toLowerCase() !== 'unlimited') {
      const capacity = parseInt(eventCapacityStr, 10);
      if (isNaN(capacity) || capacity <= 0) {
        this.setValidationRequired('validEventCapacity');
        isValid = false;
      } else {
        this.setValidationSuccess('validEventCapacity');
      }
    } else {
      this.setValidationSuccess('validEventCapacity');
    }

    return isValid;
  }

  /**
   * Validate attendee management
   */
  private validateAttendeeManagement(component: any): boolean {
    let isValid = true;

    if (component.facility.isAttendeeCountRequired) {
      const maxNumberOfPeople = component.facility.maxNumberOfPeople;
      const maxNumberOfPeopleStr = maxNumberOfPeople != null ? String(maxNumberOfPeople).trim() : '';

      if (!maxNumberOfPeopleStr || maxNumberOfPeopleStr === '') {
        this.setValidationRequired('validMaxNumberOfPeople');
        isValid = false;
      } else {
        const maxPeople = parseInt(maxNumberOfPeopleStr, 10);
        if (isNaN(maxPeople) || maxPeople <= 0) {
          this.setValidationRequired('validMaxNumberOfPeople');
          isValid = false;
        } else {
          this.setValidationSuccess('validMaxNumberOfPeople');
        }
      }
    } else {
      this.setValidationSuccess('validMaxNumberOfPeople');
    }

    return isValid;
  }

  /**
   * Validate waitlist settings
   */
  private validateWaitlist(component: any): boolean {
    let isValid = true;
    const safeValue = this.safeStringValue(component.facility.waitlistCount);

    if (component.facility.isWaitlistEnabled) {
      if (!component.facility.waitlistCount || safeValue === '') {
        this.setValidationRequired('validWaitlistCount');
        isValid = false;
      } else {
        const waitlistCount = parseInt(component.facility.waitlistCount, 10);
        if (isNaN(waitlistCount) || waitlistCount <= 0 || waitlistCount > 3) {
          this.setValidationRequired('validWaitlistCount');
          isValid = false;
        } else {
          this.setValidationSuccess('validWaitlistCount');
        }
      }
    } else {
      this.setValidationSuccess('validWaitlistCount');
    }

    return isValid;
  }

  /**
   * Validate GST settings
   */
  private validateGST(component: any): boolean {
    let isValid = true;
    const trimmedGSTValue = this.safeStringValue(component.facility.GSTValue);

    if (component.facility.isGSTApplicable) {
      if (!component.facility.GSTValue || trimmedGSTValue === '') {
        this.setValidationRequired('GSTValue');
        isValid = false;
      } else {
        const gstValue = parseFloat(component.facility.GSTValue);
        if (isNaN(gstValue) || gstValue < 0 || gstValue > 100) {
          this.setValidationRequired('GSTValue');
          isValid = false;
        } else {
          this.setValidationSuccess('GSTValue');
        }
      }
    } else {
      this.setValidationSuccess('GSTValue');
    }

    return isValid;
  }

  /**
   * Validate notification settings
   */
  private validateNotificationSettings(component: any): boolean {
    let isValid = true;

    if (!component.facility.reminder || !component.facility.reminder.value || component.facility.reminder.value === '') {
      this.setValidationRequired('validReminder');
      isValid = false;
    } else {
      this.setValidationSuccess('validReminder');
    }

    if (!component.facility.notificationAcceptanceHour || component.facility.notificationAcceptanceHour === '') {
      this.setValidationRequired('validNotificationAcceptanceHour');
      isValid = false;
    } else {
      this.setValidationSuccess('validNotificationAcceptanceHour');
    }

    return isValid;
  }

  /**
   * Validate 5-tier pricing system with child pricing (Angular 2.3.1 compatible)
   */
  /**
  * Enhanced pricing validation that considers 0.00 auto-disable
  */
  private validatePricing(component: any): boolean {
    let isValid = true;
    if (!component.facility.activityCharges) {
      return true;
    }

    const pricingTiers = [
      { key: 'member', validation: 'validMemberAmount', childValidation: 'validMemberChildAmount' },
      { key: 'nonMember', validation: 'validNonMemberAmount', childValidation: 'validNonMemberChildAmount' },
      { key: 'guest', validation: 'validGuestAmount', childValidation: 'validGuestChildAmount' },
      { key: 'corpMember', validation: 'validCorpMemberAmount', childValidation: 'validCorpMemberChildAmount' },
      { key: 'corpNonMember', validation: 'validCorpNonMemberAmount', childValidation: 'validCorpNonMemberChildAmount' }
    ];

    for (let i = 0; i < pricingTiers.length; i++) {
      const tier = pricingTiers[i];
      const pricingTier = component.facility.activityCharges[tier.key];

      if (pricingTier && pricingTier.enabled) {
        const adultAmountStr = pricingTier.amount != null ? String(pricingTier.amount).trim() : '';

        // Check if amount is 0.00 - if so, auto-disable
        const numericAmount = parseFloat(adultAmountStr);
        if (numericAmount === 0) {
          pricingTier.enabled = false;
          component.updateToggleUI(tier.key, false);
          console.log(`Auto-disabled ${tier.key} pricing: amount is 0.00`);
          continue; // Skip validation for disabled tier
        }

        // Validate adult pricing
        if (!adultAmountStr || adultAmountStr === '') {
          this.setValidationRequired(tier.validation);
          isValid = false;
        } else {
          const amount = parseFloat(adultAmountStr);
          if (isNaN(amount) || amount < 0) {
            this.setValidationRequired(tier.validation);
            isValid = false;
          } else {
            this.setValidationSuccess(tier.validation);
          }
        }

        // Validate child pricing if enabled
        if (tier.childValidation && pricingTier.childEnabled) {
          const childAmountStr = pricingTier.childAmount != null ? String(pricingTier.childAmount).trim() : '';
          const childNumericAmount = parseFloat(childAmountStr);

          // Check if child amount is 0.00 - if so, auto-disable
          if (childNumericAmount === 0) {
            pricingTier.childEnabled = false;
            component.updateChildToggleUI(tier.key, false);
            console.log(`Auto-disabled ${tier.key} child pricing: amount is 0.00`);
            continue;
          }

          if (!childAmountStr || childAmountStr === '') {
            this.setValidationRequired(tier.childValidation);
            isValid = false;
          } else {
            const childAmount = parseFloat(childAmountStr);
            if (isNaN(childAmount) || childAmount < 0) {
              this.setValidationRequired(tier.childValidation);
              isValid = false;
            } else {
              this.setValidationSuccess(tier.childValidation);
            }
          }
        } else if (tier.childValidation) {
          this.setValidationSuccess(tier.childValidation);
        }
      } else {
        this.setValidationSuccess(tier.validation);
        if (tier.childValidation) {
          this.setValidationSuccess(tier.childValidation);
        }
      }
    }

    return isValid;
  }


  /**
   * Individual field validation methods
   */
  validateFacility(component: any, fieldName: string, value: any): boolean {
    switch (fieldName) {
      case 'facility.eventType':
        return this.validateEventTypeValue(value);
      case 'facility.name':
        return this.validateEventNameValue(value);
      case 'facility.date':
        return this.validateEventDateValue(value);
      case 'facility.addressLine1':
        return this.validateAddressLine1Value(value);
      case 'facility.pincode':
        return this.validatePincodeWithCountry(value, component);
      case 'facility.country':
        return this.validateCountryValue(value);
      // case 'facility.maxBookingsPerSlot':
      //   return this.validateMaxBookingPerSlotValue(value);
      case 'facility.eventCapacity':
        return this.validateEventCapacityValue(value);
      case 'facility.waitlistCount':
        return this.validateWaitlistCountValue(value);
      case 'facility.GSTValue':
        return this.validateGSTValueValue(value);
      case 'icon':
        return this.validateIconValue(value);
      case 'facility.activityCharges.member.childAmount':
        return this.validateMemberChildAmountValue(value);
      case 'facility.activityCharges.nonMember.childAmount':
        return this.validateNonMemberChildAmountValue(value);
      case 'facility.activityCharges.guest.childAmount':
        return this.validateGuestChildAmountValue(value);
      case 'facility.activityCharges.corpMember.childAmount':
        return this.validateCorpMemberChildAmountValue(value);
      case 'facility.activityCharges.corpNonMember.childAmount':
        return this.validateCorpNonMemberChildAmountValue(value);
      default:
        return true;

    }
  }

  // Helper method in event-validation.ts
  private safeStringValue(value: any): string {
    if (typeof value === 'string') {
      return value.trim();
    } else if (typeof value === 'number') {
      return value.toString();
    } else {
      return String(value || '').trim();
    }
  }

  private validateMemberChildAmountValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validMemberChildAmount');
      return false;
    }

    const amount = parseFloat(valueStr);
    if (isNaN(amount) || amount < 0) {
      this.setValidationRequired('validMemberChildAmount');
      return false;
    }

    this.setValidationSuccess('validMemberChildAmount');
    return true;
  }

  private validateNonMemberChildAmountValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validNonMemberChildAmount');
      return false;
    }

    const amount = parseFloat(valueStr);
    if (isNaN(amount) || amount < 0) {
      this.setValidationRequired('validNonMemberChildAmount');
      return false;
    }

    this.setValidationSuccess('validNonMemberChildAmount');
    return true;
  }

  private validateGuestChildAmountValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validGuestChildAmount');
      return false;
    }

    const amount = parseFloat(valueStr);
    if (isNaN(amount) || amount < 0) {
      this.setValidationRequired('validGuestChildAmount');
      return false;
    }

    this.setValidationSuccess('validGuestChildAmount');
    return true;
  }

  private validateCorpMemberChildAmountValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validCorpMemberChildAmount');
      return false;
    }

    const amount = parseFloat(valueStr);
    if (isNaN(amount) || amount < 0) {
      this.setValidationRequired('validCorpMemberChildAmount');
      return false;
    }

    this.setValidationSuccess('validCorpMemberChildAmount');
    return true;
  }

  private validateCorpNonMemberChildAmountValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validCorpNonMemberChildAmount');
      return false;
    }

    const amount = parseFloat(valueStr);
    if (isNaN(amount) || amount < 0) {
      this.setValidationRequired('validCorpNonMemberChildAmount');
      return false;
    }

    this.setValidationSuccess('validCorpNonMemberChildAmount');
    return true;
  }

  validateFacilityTimings(component: any, fieldName: string, value: any): boolean {
    switch (fieldName) {
      case 'openTime_hour':
        return this.validateOpenTimeHourValue(value);
      case 'openTime_minute':
        return this.validateOpenTimeMinuteValue(value);
      case 'closeTime_hour':
        return this.validateCloseTimeHourValue(value);
      case 'closeTime_minute':
        return this.validateCloseTimeMinuteValue(value);
      default:
        return true;
    }
  }

  validateFacilityMaxAttendees(component: any, fieldName: string, value: any): boolean {
    if (fieldName === 'facility.maxNumberOfPeople') {
      return this.validateMaxPeopleValue(value);
    }
    return true;
  }

  validateFacilityReminderNotification(component: any, fieldName: string, value: any): boolean {
    switch (fieldName) {
      case 'facility.reminder.value':
        return this.validateReminderValueValue(value);
      case 'facility.notificationAcceptanceHour':
        return this.validateNotificationAcceptanceValue(value);
      default:
        return true;
    }
  }

  // Individual validation method implementations
  private validateEventTypeValue(value: string): boolean {
    if (!value || value.trim() === '') {
      this.setValidationRequired('validEventType');
      return false;
    }
    this.setValidationSuccess('validEventType');
    return true;
  }

  private validateEventNameValue(value: string): boolean {
    if (!value || value.trim() === '' || value.trim().length < 1) {
      this.setValidationRequired('validName');
      return false;
    }
    this.setValidationSuccess('validName');
    return true;
  }

  private validateEventDateValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validDate');
      return false;
    }

    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      this.setValidationRequired('validDate');
      return false;
    }

    this.setValidationSuccess('validDate');
    return true;
  }

  private validateAddressLine1Value(value: string): boolean {
    if (!value || value.trim() === '' || value.trim().length < 1) {
      this.setValidationRequired('validAddressLine1');
      return false;
    }
    this.setValidationSuccess('validAddressLine1');
    return true;
  }

  private validateCountryValue(value: string): boolean {
    if (!value || value.trim() === '') {
      this.setValidationRequired('validCountry');
      return false;
    }
    this.setValidationSuccess('validCountry');
    return true;
  }

  private validateWaitlistCountValue(value: string): boolean {
    const safeWaitValue = this.safeStringValue(value);
    if (!safeWaitValue || safeWaitValue.trim() === '') {
      this.setValidationRequired('validWaitlistCount');
      return false;
    }

    const count = parseInt(safeWaitValue, 10);
    if (isNaN(count) || count <= 0 || count > 3) {
      this.setValidationRequired('validWaitlistCount');
      return false;
    }

    this.setValidationSuccess('validWaitlistCount');
    return true;
  }

  // Complete validateGSTValueValue function
  validateGSTValueValue(value: any): boolean {
    const cleanValue = this.safeStringValue(value);

    // If empty, it's valid (assuming GST is optional when enabled)
    if (!cleanValue || cleanValue === '') {
      return true;
    }

    // Convert to number for validation
    const numValue = parseFloat(cleanValue);

    // Check if it's a valid number
    if (isNaN(numValue)) {
      return false;
    }

    // GST should be between 0 and 100 (percentage)
    if (numValue < 0 || numValue > 100) {
      return false;
    }

    // Check decimal places (should not exceed 2 decimal places)
    const decimalPart = cleanValue.split('.')[1];
    if (decimalPart && decimalPart.length > 2) {
      return false;
    }

    // All validations passed
    return true;
  }

  private validateIconValue(value: any): boolean {
    if (!value) {
      this.setValidationRequired('validIcon');
      return false;
    }
    this.setValidationSuccess('validIcon');
    return true;
  }

  private validateOpenTimeHourValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validOpenTime_hour');
      return false;
    }
    this.setValidationSuccess('validOpenTime_hour');
    return true;
  }

  private validateOpenTimeMinuteValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validOpenTime_minute');
      return false;
    }
    this.setValidationSuccess('validOpenTime_minute');
    return true;
  }

  private validateCloseTimeHourValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validCloseTime_hour');
      return false;
    }
    this.setValidationSuccess('validCloseTime_hour');
    return true;
  }

  private validateCloseTimeMinuteValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validCloseTime_minute');
      return false;
    }
    this.setValidationSuccess('validCloseTime_minute');
    return true;
  }

  // private validateMaxBookingPerSlotValue(value: any): boolean {
  //   const valueStr = value != null ? String(value).trim() : '';

  //   if (!valueStr || valueStr === '') {
  //     this.setValidationRequired('validmaxNumberOfPeoplePerSlot');
  //     return false;
  //   }

  //   if (valueStr.toLowerCase() !== 'unlimited') {
  //     const capacity = parseInt(valueStr, 10);
  //     if (isNaN(capacity) || capacity <= 0) {
  //       this.setValidationRequired('validmaxNumberOfPeoplePerSlot');
  //       return false;
  //     }
  //   }

  //   this.setValidationSuccess('validmaxNumberOfPeoplePerSlot');
  //   return true;
  // }

  private validateEventCapacityValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validEventCapacity');
      return false;
    }

    if (valueStr.toLowerCase() !== 'unlimited') {
      const capacity = parseInt(valueStr, 10);
      if (isNaN(capacity) || capacity <= 0) {
        this.setValidationRequired('validEventCapacity');
        return false;
      }
    }

    this.setValidationSuccess('validEventCapacity');
    return true;
  }

  private validateMaxPeopleValue(value: any): boolean {
    const valueStr = value != null ? String(value).trim() : '';

    if (!valueStr || valueStr === '') {
      this.setValidationRequired('validMaxNumberOfPeople');
      return false;
    }

    const maxPeople = parseInt(valueStr, 10);
    if (isNaN(maxPeople) || maxPeople <= 0) {
      this.setValidationRequired('validMaxNumberOfPeople');
      return false;
    }

    this.setValidationSuccess('validMaxNumberOfPeople');
    return true;
  }

  private validateReminderValueValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validReminder');
      return false;
    }
    this.setValidationSuccess('validReminder');
    return true;
  }

  private validateNotificationAcceptanceValue(value: string): boolean {
    if (!value || value === '') {
      this.setValidationRequired('validNotificationAcceptanceHour');
      return false;
    }
    this.setValidationSuccess('validNotificationAcceptanceHour');
    return true;
  }

  /**
   * Helper methods for setting validation states
   */
  private setValidationRequired(field: string): void {
    this.validate[field] = 'required';
  }

  private setValidationSuccess(field: string): void {
    this.validate[field] = '';
  }

  /**
   * Set proper values to event object before saving
   */
  setProperValuesToFacilityObj(component: any): void {
    if (component.openTime_hour && component.openTime_minute) {
      component.facility.timings.openTime = component.openTime_hour + ':' + component.openTime_minute;
    }

    if (component.closeTime_hour && component.closeTime_minute) {
      component.facility.timings.closeTime = component.closeTime_hour + ':' + component.closeTime_minute;
    }

    if (component.vendor) {
      if (component.vendor.subVendors && component.vendor.subVendors.length > 0) {
        let subvendor = component.vendor.subVendors[0];
        component.facility.vendorId = subvendor._id;
      } else {
        component.facility.vendorId = component.vendor._id;
      }

      component.facility.createdBy = component.vendor.email || '';
    }

    if (component.generateFullLocation) {
      component.generateFullLocation();
    }

    component.facility.category = 'Events';
    component.facility.isActive = true;
    component.facility.slotDuration = '0';

    if (component.vendor) {
      component.facility.timezoneOffset = component.vendor.timezoneOffset || new Date().getTimezoneOffset();
      component.facility.timezoneUTC = component.vendor.timezoneUTC || '';
      component.facility.timezoneAbbr = component.vendor.timezoneAbbr || '';
    }
  }

  /**
   * Clear all validation errors
   */
  clearAllValidationErrors(): void {
    const validationFields = Object.keys(this.validate);
    for (let i = 0; i < validationFields.length; i++) {
      this.validate[validationFields[i]] = '';
    }

    const errorMessageFields = Object.keys(this.errorMessages);
    for (let i = 0; i < errorMessageFields.length; i++) {
      this.errorMessages[errorMessageFields[i]] = '';
    }
  }

  /**
   * Check if any validation errors exist
   */
  hasValidationErrors(): boolean {
    const validationFields = Object.keys(this.validate);
    for (let i = 0; i < validationFields.length; i++) {
      if (this.validate[validationFields[i]] === 'required') {
        return true;
      }
    }
    return false;
  }

  /**
   * Get validation error count
   */
  getValidationErrorCount(): number {
    let errorCount = 0;
    const validationFields = Object.keys(this.validate);
    for (let i = 0; i < validationFields.length; i++) {
      if (this.validate[validationFields[i]] === 'required') {
        errorCount++;
      }
    }
    return errorCount;
  }
}



// WEBPACK FOOTER //
// ./src/app/facility/event/event-validation.ts